import { computed, defineComponent, nextTick, ref } from 'vue'
import type { PropType, ExtractPropTypes, CSSProperties, ComponentPublicInstance, ComputedRef } from 'vue'
import classNames from '../_antd_util/classNames'
import { ElTable, ElTableColumn } from 'element-plus'

import { formatDate, number2string, getSingleCodeContent, getMultiCodeContent, createRowData } from './utils'
import dayjs from 'dayjs'
import { isArray, isFunction, isString } from 'lodash-es'
import Space from '../Space'
import OrionError from '../../error/OrionError'

import useColumns from './hooks/useColumns'
import useCode from '../Pagetable/hooks/useCode'

import OStringInput from '../Input/StringInput/StringInput.vue'
import OSingleSelect from '../Input/SingleSelect/SingleSelect.vue'
import OMultiSelect from '../Input/MultiSelect/MultiSelect.vue'
import ODateInput from '../Input/DateInput/DateInput.vue'
import ONumberInput from '../Input/NumberInput/NumberInput.vue'
import OCheckbox from '../Input/Checkbox/Checkbox.vue'
import OLovInput from '../Input/LovInput/LovInput.vue'

type rowClassNameFn = (data: { rowData: any; rowIndex: number }) => string
type rowStyleFn = (data: { rowData: any; rowIndex: number }) => CSSProperties
type cellClassNameFn = (data: { rowData: any; column: { name: string; head: string }; rowIndex: number; columnIndex: number }) => string
type cellStyleFn = (data: { rowData: any; column: { name: string; head: string }; rowIndex: number; columnIndex: number }) => CSSProperties

export const edittableProps = () => ({
  data: Array,
  showRowNumber: {
    type: Boolean,
    default: true,
  },
  selectionMode: {
    type: Boolean,
    default: false,
  },
  currentRowMode: {
    type: Boolean,
    default: false,
  },
  size: String as PropType<'' | 'large' | 'default' | 'small'>,
  rowClassName: [String, Function] as PropType<string | rowClassNameFn>,
  rowStyle: [Object, Function] as PropType<CSSProperties | rowStyleFn>,
  cellClassName: [String, Function] as PropType<string | cellClassNameFn>,
  cellStyle: [Object, Function] as PropType<CSSProperties | cellStyleFn>,
  fakeTailRow: Boolean,
  tailRowTemplate: [Object, Function] as PropType<Record<string, any> | (() => Record<string, any>)>,
})

export type EdittableProps = Partial<ExtractPropTypes<ReturnType<typeof edittableProps>>>

export interface EdittableExpose {
  clearSelection: () => void
  toggleRowSelection: (row: any, selected?: boolean) => void
  toggleAllSelection: () => void
  getSelectionRows: () => any[]
  setCurrentRow: (row: any) => void
  scrollToRow: (index: number) => void
}

export type EdittableInstance = ComponentPublicInstance<EdittableProps, EdittableExpose>

const isCellEditable = (editable = true, cellEditable: any, row: any) => {
  if (!editable) {
    return false
  }
  if (!cellEditable) {
    return true
  }
  return cellEditable(row)
}

const getCodeData = (code: any, row: any) => {
  if (isString(code) || isArray(code)) {
    return useCode(code).value
  } else if (isFunction(code)) {
    return code(row)
  } else {
    return null
  }
}

export default defineComponent({
  name: 'OEdittable',
  inheritAttrs: false,
  props: edittableProps(),
  emits: ['selectionChange', 'currentRowChange', 'rowClick', 'rowDblclick', 'cellClick', 'cellDblclick'],
  setup(props, { slots, attrs, emit, expose }) {
    const elTableRef = ref()

    const style = computed(() => {
      const rowStyle: CSSProperties = {}

      return rowStyle
    })

    const columns = useColumns(slots)

    const clearSelection = () => {
      elTableRef.value.clearSelection()
    }

    const getSelectionRows = () => {
      return elTableRef.value.getSelectionRows()
    }

    const toggleRowSelection = (...args) => {
      return elTableRef.value.toggleRowSelection(...args)
    }

    const toggleAllSelection = () => {
      return elTableRef.value.toggleAllSelection()
    }

    const setCurrentRow = (...args) => {
      return elTableRef.value.setCurrentRow(...args)
    }

    const scrollToRow = (index: number) => {
      const $table = elTableRef.value!.$el
      const $row = $table.querySelectorAll('.el-table__row')[index]
      $row.scrollIntoViewIfNeeded()
    }

    expose({
      clearSelection,
      getSelectionRows,
      toggleRowSelection,
      toggleAllSelection,
      setCurrentRow,
      scrollToRow,
    })

    return () => {
      const { data } = props
      const { selectionMode = false } = props
      const { showRowNumber = true } = props
      const { currentRowMode = false } = props
      const { size = 'default' } = props
      const { rowClassName, rowStyle, cellClassName, cellStyle } = props
      const { fakeTailRow = false, tailRowTemplate } = props

      const cn = classNames('orion-edittable')

      // 每次UI更新都会执行，但data属性尚未修改，恰好符合要求
      const elData = [...(data as any[])]
      if (fakeTailRow) {
        elData.push(createRowData(tailRowTemplate))
      }

      const onCellChange = async (scope: any) => {
        const { row, $index } = scope
        if (fakeTailRow && elData.length == $index + 1) {
          data?.push(row)
          elData.push(createRowData(tailRowTemplate))
          
          await nextTick()
          scrollToRow($index + 1)
        }
      }

      const renderIndexColumn = () => {
        return (
          <ElTableColumn
            type={'index'}
            label="No"
            headerAlign={'center'}
            align={'center'}
            width={56}
            fixed={'left'}
            showOverflowTooltip={true}
            index={(index: number) => {
              return index + 1
            }}
          ></ElTableColumn>
        )
      }

      const renderSelectionColumn = () => {
        return <ElTableColumn type={'selection'} headerAlign={'center'} align={'center'} width={40} fixed={'left'}></ElTableColumn>
      }

      const renderStringColumn = (column) => {
        const { name, head, headAlign = 'center', bodyAlign, width, fixed, editable, cellEditable, maxlength, placeholder } = column
        return (
          <ElTableColumn
            label={head}
            headerAlign={headAlign}
            align={bodyAlign}
            prop={name}
            width={width}
            fixed={fixed}
            showOverflowTooltip={true}
            v-slots={{
              header: () => column.children?.head?.(),
              default: (scope: any) => {
                const { row: elRow, column: elColumn, $index: elIndex } = scope
                const isEditable = isCellEditable(editable, cellEditable, elRow)
                const cellValue = elRow[elColumn.property]

                return isEditable ? (
                  <OStringInput
                    modelValue={cellValue}
                    onUpdate:modelValue={(value) => {
                      elRow[elColumn.property] = value
                    }}
                    maxlength={maxlength}
                    placeholder={placeholder}
                    onChange={() => {
                      onCellChange(scope)
                    }}
                  ></OStringInput>
                ) : (
                  <div class="cell-stringinput">{cellValue}</div>
                )
              },
            }}
          ></ElTableColumn>
        )
      }

      const renderLovColumn = (column) => {
        const { name, head, headAlign = 'center', bodyAlign, width, fixed, editable, cellEditable, maxlength, placeholder, fillMapping } = column
        return (
          <ElTableColumn
            label={head}
            headerAlign={headAlign}
            align={bodyAlign}
            prop={name}
            width={width}
            fixed={fixed}
            showOverflowTooltip={true}
            v-slots={{
              header: () => column.children?.head?.(),
              default: (scope: any) => {
                const { row: elRow, column: elColumn } = scope
                const elChildren = column.children?.default?.({
                  rowData: elRow,
                  column: elColumn,
                  rowIndex: scope.$index,
                })
                const isEditable = isCellEditable(editable, cellEditable, elRow)
                const cellValue = elRow[elColumn.property]

                return isEditable ? (
                  <OLovInput
                    modelValue={cellValue}
                    onUpdate:modelValue={(value) => {
                      elRow[elColumn.property] = value
                    }}
                    maxlength={maxlength}
                    placeholder={placeholder}
                    onClear={() => {
                      if (isString(fillMapping)) {
                        const pairs = fillMapping.split(',')
                        for (let i = 0; i < pairs.length; i++) {
                          const dstName = pairs[i].split(':')[0].trim()
                          elRow[dstName] = undefined
                        }

                        onCellChange(scope)
                      }
                    }}
                    onSelect={(params) => {
                      if (isString(fillMapping)) {
                        const pairs = fillMapping.split(',')
                        for (let i = 0; i < pairs.length; i++) {
                          const dstName = pairs[i].split(':')[0].trim()
                          const srcName = pairs[i].split(':')[1].trim()
                          const srcValue = params[srcName]
                          elRow[dstName] = srcValue
                        }
                      } else {
                        fillMapping && fillMapping(elRow, params)
                      }

                      onCellChange(scope)
                    }}
                  >
                    {elChildren}
                  </OLovInput>
                ) : (
                  <div class="cell-lovinput">{cellValue}</div>
                )
              },
            }}
          ></ElTableColumn>
        )
      }

      const renderCheckboxColumn = (column) => {
        const { name, head, headAlign = 'center', bodyAlign = 'center', width, fixed, editable, cellEditable } = column
        return (
          <ElTableColumn
            label={head}
            headerAlign={headAlign}
            align={bodyAlign}
            prop={name}
            width={width}
            fixed={fixed}
            showOverflowTooltip={true}
            v-slots={{
              header: () => column.children?.head?.(),
              default: (scope: any) => {
                const { row: elRow, column: elColumn } = scope
                const isEditable = isCellEditable(editable, cellEditable, elRow)
                const cellValue = elRow[elColumn.property]

                return (
                  <div class="cell-checkbox">
                    <OCheckbox
                      modelValue={cellValue}
                      onUpdate:modelValue={(value) => {
                        elRow[elColumn.property] = value
                      }}
                      disabled={!isEditable}
                      onChange={() => {
                        onCellChange(scope)
                      }}
                    ></OCheckbox>
                  </div>
                )
              },
            }}
          ></ElTableColumn>
        )
      }

      const renderNumberColumn = (column) => {
        const {
          name,
          head,
          headAlign = 'center',
          bodyAlign = 'right',
          width,
          fixed,
          thousandsSeparator = true,
          percentage = false,
          editable,
          cellEditable,
          placeholder,
          min,
          max,
          step,
          stepStrictly,
          precision,
          controls = false,
          controlsPosition,
        } = column
        return (
          <ElTableColumn
            label={head}
            headerAlign={headAlign}
            align={bodyAlign}
            prop={name}
            width={width}
            fixed={fixed}
            showOverflowTooltip={true}
            v-slots={{
              header: () => column.children?.head?.(),
              default: (scope: any) => {
                const { row: elRow, column: elColumn } = scope
                const isEditable = isCellEditable(editable, cellEditable, elRow)
                const cellValue = elRow[elColumn.property]

                return isEditable ? (
                  <ONumberInput
                    modelValue={cellValue}
                    onUpdate:modelValue={(value) => {
                      elRow[elColumn.property] = value
                    }}
                    placeholder={placeholder}
                    min={min}
                    max={max}
                    step={step}
                    stepStrictly={stepStrictly}
                    precision={precision}
                    controls={controls}
                    controlsPosition={controlsPosition}
                    onChange={() => {
                      onCellChange(scope)
                    }}
                  ></ONumberInput>
                ) : (
                  <div class="cell-numberinput">{number2string(cellValue, thousandsSeparator, precision, percentage === true || percentage === '')}</div>
                )
              },
            }}
          ></ElTableColumn>
        )
      }

      const renderDateColumn = (column) => {
        const {
          name,
          head,
          headAlign = 'center',
          bodyAlign = 'center',
          width,
          fixed,
          editable,
          cellEditable,
          maxlength,
          placeholder,
          clearable = false,
          format = 'YYYYMMDDHHmmss',
          sourceFormat,
        } = column
        return (
          <ElTableColumn
            label={head}
            headerAlign={headAlign}
            align={bodyAlign}
            prop={name}
            width={width}
            fixed={fixed}
            showOverflowTooltip={true}
            v-slots={{
              header: () => column.children?.head?.(),
              default: (scope: any) => {
                const { row: elRow, column: elColumn } = scope
                const isEditable = isCellEditable(editable, cellEditable, elRow)
                const cellValue = elRow[elColumn.property]

                if (isEditable) {
                  return (
                    <ODateInput
                      modelValue={elRow[elColumn.property]}
                      onUpdate:modelValue={(value) => {
                        elRow[elColumn.property] = value
                      }}
                      maxlength={maxlength}
                      placeholder={placeholder}
                      clearable={clearable}
                      format={format}
                      sourceFormat={sourceFormat}
                      onChange={() => {
                        onCellChange(scope)
                      }}
                    ></ODateInput>
                  )
                }
                if (cellValue instanceof Date || dayjs.isDayjs(cellValue)) {
                  return <div class="cell-dateinput">{formatDate(cellValue, format)}</div>
                } else {
                  if (sourceFormat && format) {
                    return <div class="cell-dateinput">{formatDate(cellValue, sourceFormat, format)}</div>
                  } else {
                    return <div class="cell-dateinput">{cellValue}</div>
                  }
                }
              },
            }}
          ></ElTableColumn>
        )
      }

      const renderSingleSelectColumn = (column) => {
        const { name, head, headAlign = 'center', bodyAlign, width, fixed, editable, cellEditable, code, placeholder, clearable, filterable } = column
        return (
          <ElTableColumn
            label={head}
            headerAlign={headAlign}
            align={bodyAlign}
            prop={name}
            width={width}
            fixed={fixed}
            showOverflowTooltip={true}
            v-slots={{
              header: () => column.children?.head?.(),
              default: (scope: any) => {
                const { row: elRow, column: elColumn } = scope
                const isEditable = isCellEditable(editable, cellEditable, elRow)
                const cellValue = elRow[elColumn.property]

                let codeContent: string | null
                const codeData = getCodeData(code, elRow)
                if (!codeData) {
                  codeContent = null
                } else {
                  codeContent = getSingleCodeContent(codeData, cellValue)
                }

                return isEditable ? (
                  <OSingleSelect
                    modelValue={elRow[elColumn.property]}
                    onUpdate:modelValue={(value) => {
                      elRow[elColumn.property] = value
                    }}
                    code={codeData}
                    placeholder={placeholder}
                    clearable={clearable}
                    filterable={filterable}
                    onChange={() => {
                      onCellChange(scope)
                    }}
                  ></OSingleSelect>
                ) : (
                  <div class="cell-singleselect">{codeContent}</div>
                )
              },
            }}
          ></ElTableColumn>
        )
      }

      const renderMultiSelectColumn = (column) => {
        const {
          name,
          head,
          headAlign = 'center',
          bodyAlign,
          width,
          fixed,
          editable,
          cellEditable,
          code,
          placeholder,
          collapseTags = true,
          collapseTagsTooltip,
        } = column
        return (
          <ElTableColumn
            label={head}
            headerAlign={headAlign}
            align={bodyAlign}
            prop={name}
            width={width}
            fixed={fixed}
            showOverflowTooltip={true}
            v-slots={{
              header: () => column.children?.head?.(),
              default: (scope: any) => {
                const { row: elRow, column: elColumn } = scope
                const isEditable = isCellEditable(editable, cellEditable, elRow)
                const cellValue = elRow[elColumn.property]

                let codeContent: string | null
                const codeData = getCodeData(code, elRow)
                if (!codeData) {
                  codeContent = null
                } else {
                  codeContent = getMultiCodeContent(codeData, cellValue)
                }

                return isEditable ? (
                  <OMultiSelect
                    modelValue={elRow[elColumn.property]}
                    onUpdate:modelValue={(value) => {
                      elRow[elColumn.property] = value
                    }}
                    code={codeData}
                    placeholder={placeholder}
                    collapseTags={collapseTags}
                    collapseTagsTooltip={collapseTagsTooltip}
                    onChange={() => {
                      onCellChange(scope)
                    }}
                  ></OMultiSelect>
                ) : (
                  <div class="cell-multiselect">{codeContent}</div>
                )
              },
            }}
          ></ElTableColumn>
        )
      }

      const renderButtongroupColumn = (column) => {
        const { name, head, headAlign = 'center', bodyAlign, width, fixed } = column

        return (
          <ElTableColumn
            label={head}
            headerAlign={headAlign}
            align={bodyAlign}
            prop={name}
            width={width}
            fixed={fixed}
            v-slots={{
              default: (scope) => {
                const children = column.children?.default?.({
                  rowData: scope.row,
                  column,
                  rowIndex: scope.$index,
                })

                return (
                  <div class="cell-buttongroup">
                    <Space>{children}</Space>
                  </div>
                )
              },
              header: () => column.children?.head?.(),
            }}
          ></ElTableColumn>
        )
      }

      const renderColumngroupColumn = (column) => {
        const { head, headAlign = 'center' } = column
        const { children } = column

        return (
          <ElTableColumn
            label={head}
            headerAlign={headAlign}
            v-slots={{
              default: (scope) => {
                return renderColumns(children)
              },
            }}
          ></ElTableColumn>
        )
      }

      const renderDiyColumn = (column) => {
        const { name, head, headAlign = 'center', width, fixed } = column

        return (
          <ElTableColumn
            label={head}
            headerAlign={headAlign}
            prop={name}
            width={width}
            fixed={fixed}
            v-slots={{
              default: (scope) => {
                const children = column.children?.default?.({
                  rowData: scope.row,
                  column,
                  rowIndex: scope.$index,
                })

                return <div class="cell-diy">{children} </div>
              },
              header: () => column.children?.head?.(),
            }}
          ></ElTableColumn>
        )
      }

      const renderColumns = (columns) => {
        return columns?.map((c) => {
          if (c.type === 'OEdittableColumnString') {
            return renderStringColumn(c)
          } else if (c.type === 'OEdittableColumnLov') {
            return renderLovColumn(c)
          } else if (c.type === 'OEdittableColumnCheckbox') {
            return renderCheckboxColumn(c)
          } else if (c.type === 'OEdittableColumnNumber') {
            return renderNumberColumn(c)
          } else if (c.type === 'OEdittableColumnDate') {
            return renderDateColumn(c)
          } else if (c.type === 'OEdittableColumnSingleselect') {
            return renderSingleSelectColumn(c)
          } else if (c.type === 'OEdittableColumnMultiselect') {
            return renderMultiSelectColumn(c)
          } else if (c.type === 'OEdittableColumnButtongroup') {
            return renderButtongroupColumn(c)
          } else if (c.type === 'OEdittableColumngroup') {
            return renderColumngroupColumn(c)
          } else if (c.type === 'OEdittableColumnDiy') {
            return renderDiyColumn(c)
          } else {
            throw new OrionError(`OEdittable不能正确渲染'${c.type}'子组件。`)
          }
        })
      }

      return (
        <div class={[cn, attrs.class]} style={[style.value, attrs.style as CSSProperties]}>
          <ElTable
            ref={elTableRef}
            border
            data={elData}
            height={'100%'}
            style={{ width: '100%' }}
            onSelection-change={(rows) => emit('selectionChange', rows)}
            highlight-current-row={currentRowMode}
            onCurrent-change={(currentRow, oldCurrentRow) => emit('currentRowChange', currentRow, oldCurrentRow)}
            onRow-click={(row: any, column: any, event: Event) => emit('rowClick', row, { name: column.property, head: column.label }, event)}
            onRow-dblclick={(row: any, column: any, event: Event) => emit('rowDblclick', row, { name: column.property, head: column.label }, event)}
            onCell-click={(row: any, column: any, cell: any, event: Event) =>
              emit('cellClick', row, { name: column.property, head: column.label }, cell, event)
            }
            onCell-dblclick={(row: any, column: any, cell: any, event: Event) =>
              emit('cellDblclick', row, { name: column.property, head: column.label }, cell, event)
            }
            size={size}
            rowClassName={(data: { row: any; rowIndex: number }) => {
              if (typeof rowClassName === 'string') {
                return rowClassName
              } else if (typeof rowClassName === 'function') {
                return rowClassName({ rowData: data.row, rowIndex: data.rowIndex })
              } else {
                return 'row-class-name_not_valid'
              }
            }}
            rowStyle={(data: { row: any; rowIndex: number }) => {
              if (typeof rowStyle === 'function') {
                return rowStyle({ rowData: data.row, rowIndex: data.rowIndex })
              } else if (typeof rowStyle === 'object') {
                return rowStyle
              } else {
                return {}
              }
            }}
            cellClassName={(data: { row: any; column: any; rowIndex: number; columnIndex: number }) => {
              if (typeof cellClassName === 'string') {
                return cellClassName
              } else if (typeof cellClassName === 'function') {
                return cellClassName({
                  rowData: data.row,
                  column: { name: data.column.property, head: data.column.label },
                  rowIndex: data.rowIndex,
                  columnIndex: data.columnIndex,
                })
              } else {
                return 'cell-class-name_not_valid'
              }
            }}
            cellStyle={(data: { row: any; column: any; rowIndex: number; columnIndex: number }) => {
              if (typeof cellStyle === 'function') {
                return cellStyle({
                  rowData: data.row,
                  column: { name: data.column.property, head: data.column.label },
                  rowIndex: data.rowIndex,
                  columnIndex: data.columnIndex,
                })
              } else if (typeof cellStyle === 'object') {
                return cellStyle
              } else {
                return {}
              }
            }}
          >
            {showRowNumber && renderIndexColumn()}
            {selectionMode && renderSelectionColumn()}
            {renderColumns(columns.value)}
          </ElTable>
        </div>
      )
    }
  },
})
